import { useState, useRef, useEffect, useCallback } from "react";
import { useEnhancedComments, EnhancedComment } from "@/hooks/useEnhancedComments";
import { useCommentReplies } from "@/hooks/useCommentReplies";
import { useMessageReactions, QUICK_REACTIONS } from "@/hooks/useMessageReactions";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Send, AtSign, MessageSquare, ChevronDown, ChevronUp, Smile, Reply } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { MessageAttachmentUpload, MessageAttachment } from "@/components/messaging/MessageAttachmentUpload";
import { MessageAttachmentDisplay } from "@/components/messaging/MessageAttachmentDisplay";

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface EnhancedCommentsPanelProps {
  entityId: string;
  entityType: string;
  title?: string;
  className?: string;
  autoExpandAll?: boolean;
}

export const EnhancedCommentsPanel = ({
  entityId,
  entityType,
  title = "Team Discussion",
  className,
  autoExpandAll = false,
}: EnhancedCommentsPanelProps) => {
  const { comments, isLoading, addComment, isAddingComment, profiles } = useEnhancedComments(entityId, entityType);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [showMentionPopover, setShowMentionPopover] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [selectedMentions, setSelectedMentions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Auto-expand all threads when arriving from a notification
  useEffect(() => {
    if (autoExpandAll && comments.length > 0) {
      const allIds = new Set(comments.map(c => c.id));
      setExpandedReplies(allIds);
    }
  }, [autoExpandAll, comments]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data.session?.user?.id || null);
    });
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [comments.length]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredProfiles = profiles.filter(
    (p) =>
      p.full_name?.toLowerCase().includes(mentionSearch.toLowerCase()) ||
      p.email?.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const insertMention = (profile: { id: string; full_name: string | null }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = message.substring(0, cursorPos);
    const textAfter = message.substring(cursorPos);
    const atIndex = textBefore.lastIndexOf("@");

    if (atIndex !== -1) {
      const newText = textBefore.substring(0, atIndex) + `@${profile.full_name} ` + textAfter;
      setMessage(newText);
      setSelectedMentions([...selectedMentions, profile.id]);
    }

    setShowMentionPopover(false);
    setMentionSearch("");
    textarea.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);

    const cursorPos = e.target.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const atIndex = textBefore.lastIndexOf("@");

    if (atIndex !== -1 && !textBefore.substring(atIndex).includes(" ")) {
      setMentionSearch(textBefore.substring(atIndex + 1));
      setShowMentionPopover(true);
    } else {
      setShowMentionPopover(false);
      setMentionSearch("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionPopover && filteredProfiles.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredProfiles.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + filteredProfiles.length) % filteredProfiles.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredProfiles[highlightedIndex]);
      } else if (e.key === "Escape") {
        setShowMentionPopover(false);
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!message.trim() && attachments.length === 0) return;

    await addComment({
      message: message.trim(),
      attachments: attachments.map(a => ({
        name: a.name,
        url: a.url,
        type: a.type,
        size: a.size,
      })),
      mentionedUserIds: selectedMentions,
    });

    setMessage("");
    setAttachments([]);
    setSelectedMentions([]);
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const renderMessageWithMentions = (text: string) => {
    const mentionRegex = /@([A-Za-z\s]+?)(?=\s@|[.,!?;:\s]*$|[^a-zA-Z\s]|$)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(
        <span key={match.index} className="text-primary font-medium">
          @{match[1]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">{title}</h3>
          <span className="text-sm text-muted-foreground">({comments.length})</span>
        </div>
      </div>

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-4" ref={scrollRef}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mb-2 opacity-20" />
            <p>No comments yet</p>
            <p className="text-sm">Start the discussion by adding a comment below</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                entityType={entityType}
                entityId={entityId}
                isExpanded={expandedReplies.has(comment.id)}
                onToggleReplies={() => toggleReplies(comment.id)}
                replyingTo={replyingTo}
                setReplyingTo={setReplyingTo}
                setExpandedReplies={setExpandedReplies}
                getInitials={getInitials}
                renderMessageWithMentions={renderMessageWithMentions}
                profiles={profiles}
              />
            ))}
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t space-y-3">
        <MessageAttachmentUpload
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          disabled={isAddingComment}
        />

        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment... Use @ to mention someone"
            className="min-h-[80px] pr-20 resize-none"
            disabled={isAddingComment}
          />

          <div className="absolute bottom-2 right-2 flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => {
                const textarea = textareaRef.current;
                if (textarea) {
                  const cursorPos = textarea.selectionStart;
                  const newText = message.substring(0, cursorPos) + "@" + message.substring(cursorPos);
                  setMessage(newText);
                  setShowMentionPopover(true);
                  setTimeout(() => textarea.focus(), 0);
                }
              }}
            >
              <AtSign className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isAddingComment || (!message.trim() && attachments.length === 0)}
              className="h-8"
            >
              {isAddingComment ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Mention Popover */}
          {showMentionPopover && filteredProfiles.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-64 bg-popover border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
              {filteredProfiles.slice(0, 5).map((profile, index) => (
                <button
                  key={profile.id}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent",
                    index === highlightedIndex && "bg-accent"
                  )}
                  onClick={() => insertMention(profile)}
                >
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs">
                      {getInitials(profile.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{profile.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{profile.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Comment Item Component
interface CommentItemProps {
  comment: EnhancedComment;
  currentUserId: string | null;
  entityType: string;
  entityId: string;
  isExpanded: boolean;
  onToggleReplies: () => void;
  replyingTo: string | null;
  setReplyingTo: (id: string | null) => void;
  setExpandedReplies: React.Dispatch<React.SetStateAction<Set<string>>>;
  getInitials: (name: string | null | undefined) => string;
  renderMessageWithMentions: (text: string) => React.ReactNode;
  profiles: Profile[];
}

const CommentItem = ({
  comment,
  currentUserId,
  entityType,
  entityId,
  isExpanded,
  onToggleReplies,
  replyingTo,
  setReplyingTo,
  setExpandedReplies,
  getInitials,
  renderMessageWithMentions,
  profiles,
}: CommentItemProps) => {
  const { groupedReactions, toggleReaction, isLoading: reactionsLoading } = useMessageReactions(
    comment.id,
    "comment"
  );
  // Hook needs parentId when replying OR when expanded
  const needsReplies = isExpanded || replyingTo === comment.id;
  const { replies, isLoading: repliesLoading, sendReply, isSending } = useCommentReplies(
    needsReplies ? comment.id : undefined
  );
  const [replyText, setReplyText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyMentionSearch, setReplyMentionSearch] = useState("");
  const [showReplyMentionPopover, setShowReplyMentionPopover] = useState(false);
  const [replyMentions, setReplyMentions] = useState<string[]>([]);
  const [replyHighlightedIndex, setReplyHighlightedIndex] = useState(0);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const LONG_MESSAGE_THRESHOLD = 200;
  const isLongMessage = comment.message.length > LONG_MESSAGE_THRESHOLD;

  const filteredReplyProfiles = profiles.filter(
    (p) =>
      p.full_name?.toLowerCase().includes(replyMentionSearch.toLowerCase()) ||
      p.email?.toLowerCase().includes(replyMentionSearch.toLowerCase())
  );

  const insertReplyMention = (profile: Profile) => {
    const textarea = replyTextareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = replyText.substring(0, cursorPos);
    const textAfter = replyText.substring(cursorPos);
    const atIndex = textBefore.lastIndexOf("@");

    if (atIndex !== -1) {
      const newText = textBefore.substring(0, atIndex) + `@${profile.full_name} ` + textAfter;
      setReplyText(newText);
      setReplyMentions([...replyMentions, profile.id]);
    }

    setShowReplyMentionPopover(false);
    setReplyMentionSearch("");
    textarea.focus();
  };

  const handleReplyInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setReplyText(value);

    const cursorPos = e.target.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const atIndex = textBefore.lastIndexOf("@");

    if (atIndex !== -1 && !textBefore.substring(atIndex).includes(" ")) {
      setReplyMentionSearch(textBefore.substring(atIndex + 1));
      setShowReplyMentionPopover(true);
    } else {
      setShowReplyMentionPopover(false);
      setReplyMentionSearch("");
    }
  };

  const handleReplyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showReplyMentionPopover && filteredReplyProfiles.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setReplyHighlightedIndex((prev) => (prev + 1) % filteredReplyProfiles.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setReplyHighlightedIndex((prev) => (prev - 1 + filteredReplyProfiles.length) % filteredReplyProfiles.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertReplyMention(filteredReplyProfiles[replyHighlightedIndex]);
      } else if (e.key === "Escape") {
        setShowReplyMentionPopover(false);
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    
    await sendReply({
      message: replyText.trim(),
      entityType,
      entityId,
      departmentId: comment.department_id,
      mentionedUserIds: replyMentions,
    });
    
    setReplyText("");
    setReplyMentions([]);
    setReplyingTo(null);
    // Auto-expand to show the new reply
    setExpandedReplies((prev) => new Set(prev).add(comment.id));
  };

  const reactions = currentUserId ? groupedReactions(currentUserId) : [];

  return (
    <div className="group">
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarFallback className="text-xs">
            {getInitials(comment.sender?.full_name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">
              {comment.sender?.full_name || "Unknown"}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
            </span>
          </div>

          <div 
            className="mt-1 text-sm whitespace-pre-wrap break-words max-h-[120px] overflow-y-auto scrollable-content pr-1"
            style={{ touchAction: 'pan-y' }}
          >
            {renderMessageWithMentions(comment.message)}
          </div>

          {/* Attachments */}
          {comment.attachments && comment.attachments.length > 0 && (
            <div className="mt-2">
              <MessageAttachmentDisplay attachments={comment.attachments} />
            </div>
          )}

          {/* Reactions */}
          {reactions.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {reactions.map((reaction) => (
                <button
                  key={reaction.emoji}
                  onClick={() => currentUserId && toggleReaction(reaction.emoji, currentUserId)}
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors",
                    reaction.hasReacted
                      ? "bg-primary/10 border-primary/30 text-primary"
                      : "bg-muted hover:bg-muted/80"
                  )}
                >
                  <span>{reaction.emoji}</span>
                  <span>{reaction.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Emoji Picker */}
            <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2">
                  <Smile className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="start">
                <div className="flex flex-wrap gap-1 max-w-[200px]">
                  {QUICK_REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => {
                        if (currentUserId) {
                          toggleReaction(emoji, currentUserId);
                        }
                        setShowEmojiPicker(false);
                      }}
                      className="p-1 hover:bg-accent rounded text-lg"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Reply Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
            >
              <Reply className="h-4 w-4 mr-1" />
              Reply
            </Button>
          </div>

          {/* Reply Count & Toggle */}
          {comment.reply_count > 0 && (
            <button
              onClick={onToggleReplies}
              className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
            >
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              {comment.reply_count} {comment.reply_count === 1 ? "reply" : "replies"}
            </button>
          )}

          {/* Replies */}
          {isExpanded && (
            <div className="mt-3 ml-4 pl-4 border-l-2 border-muted space-y-3">
              {repliesLoading ? (
                <div className="flex items-center gap-2 py-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading replies...</span>
                </div>
              ) : (
                replies.map((reply) => (
                  <div key={reply.id} className="flex gap-2">
                    <Avatar className="h-6 w-6 flex-shrink-0">
                      <AvatarFallback className="text-xs">
                        {getInitials(reply.sender?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {reply.sender?.full_name || "Unknown"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm mt-0.5">{renderMessageWithMentions(reply.message)}</p>
                      {reply.attachments && reply.attachments.length > 0 && (
                        <div className="mt-1">
                          <MessageAttachmentDisplay attachments={reply.attachments} />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Reply Input */}
          {replyingTo === comment.id && (
            <div className="mt-3 ml-4 pl-4 border-l-2 border-primary/30">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Textarea
                    ref={replyTextareaRef}
                    value={replyText}
                    onChange={handleReplyInputChange}
                    placeholder="Write a reply... Use @ to mention someone"
                    className="min-h-[60px] text-sm resize-none"
                    onKeyDown={handleReplyKeyDown}
                  />
                  {/* Reply Mention Popover */}
                  {showReplyMentionPopover && filteredReplyProfiles.length > 0 && (
                    <div className="absolute bottom-full left-0 mb-1 w-56 bg-popover border rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                      {filteredReplyProfiles.slice(0, 5).map((profile, index) => (
                        <button
                          key={profile.id}
                          className={cn(
                            "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent text-sm",
                            index === replyHighlightedIndex && "bg-accent"
                          )}
                          onClick={() => insertReplyMention(profile)}
                        >
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-xs">
                              {getInitials(profile.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{profile.full_name}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    onClick={handleSendReply}
                    disabled={isSending || !replyText.trim()}
                    className="h-8"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyText("");
                      setReplyMentions([]);
                    }}
                    className="h-8 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedCommentsPanel;
